import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { localeConfig, supportedLanguages } from '../../i18n/locales';
import { useAuth } from '../../context/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

/**
 * Language Flag Button - แสดงแค่ธงชาติสำหรับเปลี่ยนภาษา
 * ใช้เป็น shortcut ใน Navbar
 */
const LanguageFlagButton = () => {
  const { i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentLang = i18n.language || 'th';
  const currentLocale = localeConfig[currentLang] || localeConfig['th'];

  const changeLanguage = async (lng) => {
    // Change language in i18n (saves to localStorage automatically)
    i18n.changeLanguage(lng);
    setIsOpen(false);

    // Sync to Firestore if user is logged in
    if (currentUser?.uid) {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
          language: lng,
          updatedAt: new Date()
        });
      } catch (error) {
        console.error('Error saving language to Firestore:', error);
        // Don't show error to user, localStorage is enough
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Flag Button - Click to show dropdown */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors flex items-center justify-center"
        title={`${currentLocale.nativeName} - คลิกเพื่อเลือกภาษา`}
        aria-label="Change language"
      >
        <span className="text-2xl">{currentLocale.flag}</span>
      </button>
      
      {/* Dropdown Menu - Show all languages */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[200px] max-h-[400px] overflow-y-auto">
          {supportedLanguages.map((lng) => {
            const locale = localeConfig[lng];
            const isSelected = currentLang === lng;
            return (
              <button
                key={lng}
                onClick={() => changeLanguage(lng)}
                className={`w-full text-left px-4 py-2 flex items-center gap-2 transition ${
                  isSelected 
                    ? 'bg-blue-100 text-blue-800 font-semibold' 
                    : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <span className="text-lg">{locale.flag}</span>
                <span className="text-xs font-mono text-gray-500 mr-1">{locale.code}</span>
                <span className={isSelected ? 'font-semibold' : 'font-normal'}>{locale.nativeName}</span>
                {isSelected && (
                  <span className="ml-auto text-blue-600">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageFlagButton;

